import { useCallback, useState } from 'react';

import { GQLResponsePerimeterXChallengeError } from '@change-corgi/core/gql';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { CancellationSource } from '#src/app/shared/constants';

import { cancelMembership } from './api';

export type CancelMembershipResult = [
	{ cancelled: boolean; cancelError: string | undefined },
	(id: string, token?: string) => Promise<void>,
];

export function useCancelMembership({
	cancellationSource,
}: {
	cancellationSource?: CancellationSource;
} = {}): CancelMembershipResult {
	const utilityContext = useUtilityContext();

	const [cancelled, setCancelled] = useState(false);
	const [cancelError, setCancelError] = useState<string | undefined>(undefined);

	const cancel = useCallback(
		async (id: string, token?: string) => {
			try {
				await cancelMembership(id, cancellationSource, token, utilityContext);
				setCancelError(undefined);

				// TODO: Remove timeout after addressing cancellation webhook delay https://change.atlassian.net/browse/CHANGE-50777
				setTimeout(() => setCancelled(true), 1500);
			} catch (e) {
				if (e instanceof GQLResponsePerimeterXChallengeError) throw e;

				const message = e instanceof Error ? e.message : String(e);
				setCancelError(`An error occurred trying to cancel the membership: ${message}`);
			}
		},
		[cancellationSource, utilityContext],
	);

	return [{ cancelled, cancelError }, cancel];
}
